import { create } from 'zustand';
import { WebSocketHeartbeat } from '../../utils/websocketHeartbeat';

interface WebSocketState {
  tradePrice: string | null;
  tradeQuantity: string | null;
  tradeId: number | null;
  tradeTimestamp: number | null;
  tradeIsBuyerMaker: boolean | null;

  depthAsks: any[] | null;
  depthBids: any[] | null;

  tickerHigh: string | null;
  tickerLow: string | null;
  tickerChange: string | null;
  tickerVolume: string | null;

  markPrice: string | null;
  markPriceIndex: string | null;
  markPriceRate: string | null;
  markPriceTime: string | null;

   openInterest: string | null;

  setSymbol: (symbol: string) => void;
  closeSocket: () => void;
}

export const useCiTradeWebSocketStore = create<WebSocketState>((set, get) => {
  let socket: WebSocket | null = null;
  let currentSymbol = '';
  let activeConsumers = 0;
  let isConnecting = false;
  let isFlushing = false;
  let reconnectAttempts = 0;
  const maxReconnects = 10;
  let reconnectDelay = 1000; // Initial delay: 1 second
  const maxReconnectDelay = 30000; // Max delay: 30 seconds
  const FLUSH_THROTTLE_TIME = 50;
  let hasPendingUpdate = false;
  let reconnectTimer: any = null;

  let flushTimer: any = null;
  // let visibilityInterval: any = null;

  let oiTimer: any = null;
  let heartbeat: WebSocketHeartbeat | null = null;

const startOiPoll = (symLower: string) => {
  if (oiTimer) clearInterval(oiTimer);
  const sym = symLower.toUpperCase();

  if (!sym) return;

  const fetchOI = async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const res = await fetch(`https://fapi.binance.com/fapi/v1/openInterest?symbol=${sym}`);
      if (!res.ok) return;
      const json = await res.json(); 
      latestData.openInterest = json.openInterest;
      hasPendingUpdate = true;
      scheduleFlush();
    } catch (e) {
      console.warn('[BinanceWebSocketStore] OI fetch failed:', e);
    }
  };

  fetchOI();              
  oiTimer = setInterval(fetchOI, 2000); 
};

  const latestData: {
    tradePrice: string | null;
    tradeQuantity: string | null;
    tradeId: number | null;
    tradeTimestamp: number | null;
    tradeIsBuyerMaker: boolean | null;

    depthAsks: any[] | null;
    depthBids: any[] | null;

    tickerHigh: string | null;
    tickerLow: string | null;
    tickerChange: string | null;
    tickerVolume: string | null;

    markPrice: string | null;
    markPriceIndex: string | null;
    markPriceRate: string | null;
    markPriceTime: string | null;

     openInterest: string | null;
  } = {
    tradePrice: null,
    tradeQuantity: null,
    tradeId: null,
    tradeTimestamp: null,
    tradeIsBuyerMaker: null,

    depthAsks: null,
    depthBids: null,

    tickerHigh: null,
    tickerLow: null,
    tickerChange: null,
    tickerVolume: null,

    markPrice: null,
    markPriceIndex: null,
    markPriceRate: null,
    markPriceTime: null,

     openInterest: null,
  };


  const flush = () => {
    if (document.visibilityState === 'hidden') return;

    if (!hasPendingUpdate) return;
    hasPendingUpdate = false;

    if (isFlushing) return;
    isFlushing = true;

    const start = performance.now();

    try {
      const prevState = get();
      const updates: Partial<WebSocketState> = {};

      if (prevState.tradePrice !== latestData.tradePrice) updates.tradePrice = latestData.tradePrice;
      if (prevState.tradeQuantity !== latestData.tradeQuantity) updates.tradeQuantity = latestData.tradeQuantity;
      if (prevState.tradeId !== latestData.tradeId) updates.tradeId = latestData.tradeId;
      if (prevState.tradeTimestamp !== latestData.tradeTimestamp) updates.tradeTimestamp = latestData.tradeTimestamp;
      if (prevState.tradeIsBuyerMaker !== latestData.tradeIsBuyerMaker) updates.tradeIsBuyerMaker = latestData.tradeIsBuyerMaker;
      if (prevState.depthAsks !== latestData.depthAsks) updates.depthAsks = latestData.depthAsks;
      if (prevState.depthBids !== latestData.depthBids) updates.depthBids = latestData.depthBids;
      if (prevState.tickerHigh !== latestData.tickerHigh) updates.tickerHigh = latestData.tickerHigh;
      if (prevState.tickerLow !== latestData.tickerLow) updates.tickerLow = latestData.tickerLow;
      if (prevState.tickerChange !== latestData.tickerChange) updates.tickerChange = latestData.tickerChange;
      if (prevState.tickerVolume !== latestData.tickerVolume) updates.tickerVolume = latestData.tickerVolume;
      if (prevState.markPrice !== latestData.markPrice) updates.markPrice = latestData.markPrice;
      if (prevState.markPriceIndex !== latestData.markPriceIndex) updates.markPriceIndex = latestData.markPriceIndex;
      if (prevState.markPriceRate !== latestData.markPriceRate) updates.markPriceRate = latestData.markPriceRate;
      if (prevState.markPriceTime !== latestData.markPriceTime) updates.markPriceTime = latestData.markPriceTime;
      if (prevState.openInterest !== latestData.openInterest) updates.openInterest = latestData.openInterest;


      if (Object.keys(updates).length > 0) {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => set(updates), { timeout: 200 });
        } else {
          set(updates);
        }
      }
    } catch (e) {
      console.error('[BinanceWebSocketStore] flush error:', e);
    } finally {
      const duration = performance.now() - start;
      if (duration > 16.67) {
        console.warn(`[BinanceWebSocketStore] flush took issue ${duration.toFixed(2)}ms`);
      } else {
        // console.log(`[BinanceWebSocketStore] flush took ${duration.toFixed(2)}ms`);
      }
      isFlushing = false;
    }
  };


  // const flush = () => {
  //   if (!socket || isFlushing) return;
  //   isFlushing = true;
  //
  //   const prevState = get();
  //   const updates: Partial<WebSocketState> = {};
  //
  //   if (prevState.tradePrice !== latestData.tradePrice) updates.tradePrice = latestData.tradePrice;
  //   if (prevState.tradeQuantity !== latestData.tradeQuantity) updates.tradeQuantity = latestData.tradeQuantity;
  //   if (prevState.tradeId !== latestData.tradeId) updates.tradeId = latestData.tradeId;
  //   if (prevState.tradeTimestamp !== latestData.tradeTimestamp) updates.tradeTimestamp = latestData.tradeTimestamp;
  //   if (prevState.tradeIsBuyerMaker !== latestData.tradeIsBuyerMaker) updates.tradeIsBuyerMaker = latestData.tradeIsBuyerMaker;
  //   if (prevState.depthAsks !== latestData.depthAsks) updates.depthAsks = latestData.depthAsks;
  //   if (prevState.depthBids !== latestData.depthBids) updates.depthBids = latestData.depthBids;
  //   if (prevState.tickerHigh !== latestData.tickerHigh) updates.tickerHigh = latestData.tickerHigh;
  //   if (prevState.tickerLow !== latestData.tickerLow) updates.tickerLow = latestData.tickerLow;
  //   if (prevState.tickerChange !== latestData.tickerChange) updates.tickerChange = latestData.tickerChange;
  //   if (prevState.tickerVolume !== latestData.tickerVolume) updates.tickerVolume = latestData.tickerVolume;
  //   if (prevState.markPrice !== latestData.markPrice) updates.markPrice = latestData.markPrice;
  //   if (prevState.markPriceIndex !== latestData.markPriceIndex) updates.markPriceIndex = latestData.markPriceIndex;
  //   if (prevState.markPriceRate !== latestData.markPriceRate) updates.markPriceRate = latestData.markPriceRate;
  //   if (prevState.markPriceTime !== latestData.markPriceTime) updates.markPriceTime = latestData.markPriceTime;
  //
  //   if (Object.keys(updates).length > 0) {
  //     requestIdleCallback(() => {
  //       set(updates);
  //     });
  //   }
  //
  //   isFlushing = false;
  //   rafPending = false;
  // };


  const scheduleFlush = () => {
    if (document.visibilityState === 'hidden') return;
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      if (document.visibilityState === 'visible') flush();
      flushTimer = null;
    }, FLUSH_THROTTLE_TIME);
  };

  // const scheduleFlush = () => {
  //   if (rafPending || isFlushing) return;
  //   rafPending = true;
  //
  //   const callback = () => {
  //     flush();
  //     rafPending = false; // 중복 예약 방지
  //   };
  //
  //   if (document.visibilityState === 'visible') {
  //     requestAnimationFrame(callback);
  //   } else {
  //     setTimeout(callback, 3000);
  //   }
  // };

  const handleVisibilityChange = () => {
    console.log('[BinanceWebSocketStore] visibility changed:', document.visibilityState);
    if (document.visibilityState === 'visible') {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        setupWebSocket(currentSymbol);
      }
       if (currentSymbol) startOiPoll(currentSymbol);
      flush();
    }
  };


  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // if (!hasInitialized && typeof window !== 'undefined') {
  //   hasInitialized = true;
  //   document.addEventListener('visibilitychange', handleVisibilityChange);
  //   visibilityInterval = setInterval(() => {
  //     if (document.visibilityState === 'hidden') {
  //       flush();
  //     }
  //   }, 30000);
  // }

  const setupWebSocket = (symbol: string) => {
    const lowerSymbol = symbol.toLowerCase();
    const isSameSymbol = currentSymbol === lowerSymbol;

    if (isSameSymbol && socket && socket.readyState === WebSocket.OPEN) return;

    // ✅ Check max reconnection attempts
    if (reconnectAttempts >= maxReconnects) {
      console.error('[BinanceWebSocketStore] Max reconnection attempts reached. Stopping reconnection.');
      isConnecting = false;
      return;
    }

    isConnecting = true;
    currentSymbol = lowerSymbol;

    // Clear any pending reconnection timer
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    // 기존 소켓 종료
    if (socket) {
      try {
        socket.close();
      } catch (e) {
        console.warn('[BinanceWebSocketStore] Socket close error:', e);
      }
      socket = null;
    }

    console.log(`[BinanceWebSocketStore] Connecting... (attempt ${reconnectAttempts + 1}/${maxReconnects}, delay: ${reconnectDelay}ms)`);

    const ws = new WebSocket(import.meta.env.VITE_APP_STREAM_URL || 'wss://dev-stream.bynova.net');

    ws.onopen = () => {
      console.log('[BinanceWebSocketStore] WebSocket connected successfully');

      // ✅ Reset reconnection state on successful connection
      reconnectAttempts = 0;
      reconnectDelay = 1000;
      isConnecting = false;

      // ✅ Heartbeat 시작
      heartbeat = new WebSocketHeartbeat(
        ws,
        () => setupWebSocket(currentSymbol), // Pong 타임아웃 시 재연결
        'CiTradeWebSocket'
      );
      heartbeat.start(30000, 5000);

      // 기본 채널 구독 (필요한 것만 선택해도 됨)
      ws.send(JSON.stringify({ type: 'subscribe', symbol: lowerSymbol, channel: 'all' }));
      // const channels = ['aggTrade', 'depth10', 'ticker', 'markPrice'];
      // for (const ch of channels) {
      //   ws.send(JSON.stringify({ type: 'subscribe', symbol: lowerSymbol, channel: ch }));
      // }
    };

    ws.onmessage = (event) => {
      try {
        const { channel, data } = JSON.parse(event.data);

        // ✅ Heartbeat: Pong 메시지 처리
        if (channel === 'pong' || data?.type === 'pong') {
          heartbeat?.receivedPong();
          return;
        }

        if (!channel || !data) return;

        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

        if (channel.includes(':aggTrade')) {
          latestData.tradePrice = parsedData.p;
          latestData.tradeQuantity = parsedData.q;
          latestData.tradeId = parsedData.a ?? parsedData.t;
          latestData.tradeTimestamp = parsedData.T;
          latestData.tradeIsBuyerMaker = parsedData.m;
        } else if (channel.includes(':depth')) {
          latestData.depthAsks = parsedData.a;
          latestData.depthBids = parsedData.b;
        } else if (channel.includes(':ticker')) {
          latestData.tickerHigh = parsedData.h;
          latestData.tickerLow = parsedData.l;
          latestData.tickerChange = parsedData.P;
          latestData.tickerVolume = parsedData.q;
        } else if (channel.includes(':markPrice')) {
          latestData.markPrice = parsedData.p;
          latestData.markPriceIndex = parsedData.i;
          latestData.markPriceRate = parsedData.r;
          latestData.markPriceTime = parsedData.T;
        }

        hasPendingUpdate = true;
        scheduleFlush();

      } catch (err) {
        console.error('[BinanceWebSocketStore] parse error:', err);
      }
    };

    ws.onclose = (event) => {
      const code = event.code;
      const reason = event.reason || 'Unknown';
      console.warn(`[BinanceWebSocketStore] WebSocket closed (code: ${code}, reason: ${reason})`);

      // ✅ Heartbeat 정리
      heartbeat?.stop();
      heartbeat = null;

      // ✅ Handle close codes (if backend implements custom codes in the future)
      // 4001-4002: Authentication errors → Could redirect to login
      // 4006: Insufficient balance → Show warning
      // 1000: Normal closure → No reconnection needed
      if (code === 1000) {
        console.log('[BinanceWebSocketStore] Normal closure, no reconnection');
        return;
      }

      // ✅ Exponential backoff reconnection
      if (reconnectAttempts < maxReconnects) {
        reconnectAttempts++;

        // Calculate exponential backoff delay
        const currentDelay = Math.min(
          reconnectDelay * Math.pow(2, reconnectAttempts - 1),
          maxReconnectDelay
        );

        console.log(`[BinanceWebSocketStore] Scheduling reconnection in ${currentDelay}ms (attempt ${reconnectAttempts}/${maxReconnects})`);

        reconnectTimer = setTimeout(() => {
          setupWebSocket(currentSymbol);
        }, currentDelay);
      } else {
        console.error('[BinanceWebSocketStore] Max reconnection attempts reached');
        isConnecting = false;
      }
    };

    ws.onerror = (err) => {
      console.error('[BinanceWebSocketStore] WebSocket error:', err);

      // Error handling will trigger onclose, which handles reconnection
    };

    socket = ws;

    startOiPoll(lowerSymbol);
  };


  return {
    tradePrice: null,
    tradeQuantity: null,
    tradeId: null,
    tradeTimestamp: null,
    tradeIsBuyerMaker: null,
    depthAsks: null,
    depthBids: null,
    tickerHigh: null,
    tickerLow: null,
    tickerChange: null,
    tickerVolume: null,
    markPrice: null,
    markPriceIndex: null,
    markPriceRate: null,
    markPriceTime: null,
    openInterest:null,
    setSymbol: (symbol) => {
      if (!symbol) return;
      setupWebSocket(symbol);
      activeConsumers++;
    },
    closeSocket: () => {
      if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }

      // ✅ Clear reconnection timer
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      activeConsumers = Math.max(0, activeConsumers - 1);
      if (activeConsumers === 0 && socket) {
        try {
          // ✅ Heartbeat 정리
          heartbeat?.stop();
          heartbeat = null;

          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          socket.close();
          isConnecting = false;

          // ✅ Reset reconnection state
          reconnectAttempts = 0;
          reconnectDelay = 1000;
        } catch (e) {
          console.warn('[BinanceWebSocketStore] Error closing socket:', e);
        }
        socket = null;

        // if (visibilityInterval) {
        //   clearInterval(visibilityInterval);
        //   visibilityInterval = null;
        // }
        document.removeEventListener('visibilitychange', handleVisibilityChange);

          if (oiTimer) {
      clearInterval(oiTimer);
      oiTimer = null;
    }

        latestData.tradePrice = null;
        latestData.tradeQuantity = null;
        latestData.tradeId = null;
        latestData.tradeTimestamp = null;
        latestData.tradeIsBuyerMaker = null;
        latestData.depthAsks = null;
        latestData.depthBids = null;
        latestData.tickerHigh = null;
        latestData.tickerLow = null;
        latestData.tickerChange = null;
        latestData.tickerVolume = null;
        latestData.markPrice = null;
        latestData.markPriceIndex = null;
        latestData.markPriceRate = null;
        latestData.markPriceTime = null;
        latestData.openInterest = null;
      }
    },
  };
});
